/**
 * Dates, in the engine's unit.
 *
 * A `Day` is days since 1970-01-01 UTC — the same integer the availability
 * package, the API and the tariff engine all speak. Converting at the edges and
 * doing every comparison in integers is what keeps a calendar out of the class
 * of bugs where a stay is one night long in Amsterdam and two in London: a
 * booking date has no time of day, and giving it one via the local timezone is
 * how it acquires one.
 *
 * Everything user-visible is formatted through `Intl` with an explicit
 * `timeZone: 'UTC'`, for the same reason. Omitting it renders the day before in
 * every timezone west of Greenwich.
 */

export type Day = number;

const MS = 86_400_000;

export function toDay(iso: string): Day | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const day = Math.round(Date.UTC(y, mo - 1, d) / MS);
  // Round-tripped rather than range-checked, so 2026-02-30 is refused instead
  // of silently becoming the 2nd of March.
  return toIso(day) === m[0] ? day : null;
}

export function toIso(d: Day): string {
  return new Date(d * MS).toISOString().slice(0, 10);
}

/**
 * Today, as a Day. Injectable so a test is not a function of the wall clock.
 *
 * `zone` is the PARK's, and it is the one place a clock legitimately enters. An
 * ARRIVAL is a pure calendar date and carries no time — that is why it is an
 * integer — but "what is today" is a question about an instant, and an instant
 * needs a zone to have a date at all.
 *
 * Flooring the timestamp gives the UTC day, so a guest in Amsterdam opening the
 * calculator at 00:30 local is still on yesterday as far as the calendar is
 * concerned, and the earliest arrival it offers has already passed. West of
 * Greenwich the error runs the other way and the guest is refused a day they can
 * still book.
 *
 * Absent means UTC, which is the honest answer for a park that has not said.
 */
export function today(now: number = Date.now(), zone?: string): Day {
  if (!zone) return Math.floor(now / MS);
  // `en-CA` is the one common locale whose numeric format is YYYY-MM-DD, so the
  // parts come back in an order that reads straight back as a date.
  try {
    const iso = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zone,
    }).format(new Date(now));
    return toDay(iso) ?? Math.floor(now / MS);
  } catch {
    // An unusable zone must not stop a guest searching. UTC is wrong by at most a
    // day; a thrown error is wrong by the whole page.
    return Math.floor(now / MS);
  }
}

/** Day-of-week, 0 = Sunday, matching `weekdayOf` in the availability package. */
export function weekdayOf(d: Day): number {
  return (((d + 4) % 7) + 7) % 7;
}

export function startOfMonth(d: Day): Day {
  const t = new Date(d * MS);
  return Math.round(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1) / MS);
}

export function addMonths(d: Day, n: number): Day {
  const t = new Date(d * MS);
  return Math.round(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + n, 1) / MS);
}

export function daysInMonth(d: Day): number {
  const t = new Date(d * MS);
  return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
}

/**
 * The six-week grid a month calendar draws, Monday first.
 *
 * Monday because every European park's week starts there and a Sunday-first
 * grid makes a changeover Saturday sit at the far right. A locale-driven first
 * day would be more correct in the abstract and would move the column under a
 * British guest's finger for no benefit — the grid is about the park's week,
 * not the reader's.
 */
export function monthGrid(month: Day): Day[] {
  const first = startOfMonth(month);
  const lead = (weekdayOf(first) + 6) % 7;
  const start = first - lead;
  return Array.from({ length: 42 }, (_, i) => start + i);
}

export function isSameMonth(a: Day, b: Day): boolean {
  return startOfMonth(a) === startOfMonth(b);
}

export interface Formatters {
  /** "12 Aug" */
  short: (d: Day) => string;
  /** "Wednesday 12 August 2026" */
  full: (d: Day) => string;
  /** "August 2026" */
  month: (d: Day) => string;
  /** "Mo" — the calendar's column heads. */
  weekdayShort: (weekday: number) => string;
  money: (n: number) => string;
}

/**
 * Formatters bound to a locale and a currency.
 *
 * Built once per locale rather than per cell: an `Intl.DateTimeFormat` costs
 * real milliseconds to construct and a calendar makes 42 of them per month.
 *
 * The currency is defaulted so a caller that only wants dates — the calendar —
 * need not invent one. Anything that shows a PRICE passes the park's own, from
 * configuration, because a park billing in Swiss francs is one setting and not
 * a fork.
 */
export function formatters(locale: string, currency = 'EUR'): Formatters {
  const short = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const full = new Intl.DateTimeFormat(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const wd = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const money = new Intl.NumberFormat(locale, {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return {
    short: (d) => short.format(new Date(d * MS)),
    full: (d) => full.format(new Date(d * MS)),
    month: (d) => month.format(new Date(d * MS)),
    // 1970-01-04 was a Sunday, so `3 + weekday` lands on the right day whatever
    // the weekday number is.
    weekdayShort: (weekday) => wd.format(new Date((3 + weekday) * MS)),
    money: (n) => money.format(n),
  };
}
